// src/admin/AdminReviews.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';

function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Édition inline
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState(5);
  const [editComment, setEditComment] = useState('');
  const [saving, setSaving] = useState(false);

  // Suppression en cours
  const [deletingId, setDeletingId] = useState(null);

  /* =========================================================
     CHARGEMENT DES AVIS
  ========================================================= */
  const fetchReviews = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.get('https://madadline.com/api/avis', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setReviews(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError('Impossible de charger les avis.');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  /* =========================================================
     ÉDITION
  ========================================================= */
  const startEdit = (r) => {
    setEditingId(r.id);
    setEditNote(r.note || 5);
    setEditComment(r.commentaire || r.comment || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditNote(5);
    setEditComment('');
  };

  const saveEdit = async (id) => {
    if (!editComment.trim()) {
      alert('Le commentaire ne peut pas être vide.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `https://madadline.com/api/avis/${id}`,
        { note: editNote, commentaire: editComment.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviews(prev =>
        prev.map(r =>
          r.id === id
            ? { ...r, note: editNote, commentaire: editComment.trim() }
            : r
        )
      );
      cancelEdit();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'Erreur lors de la modification.');
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     SUPPRESSION
  ========================================================= */
  const deleteReview = async (id) => {
    if (!window.confirm('Supprimer définitivement cet avis ?')) return;
    setDeletingId(id);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`https://madadline.com/api/avis/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'Erreur lors de la suppression.');
    } finally {
      setDeletingId(null);
    }
  };

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="admin-reviews">
      <header className="admin-page-head">
        <div>
          <h1>Avis clients</h1>
          <p>{reviews.length} avis</p>
        </div>
        <button
          className="admin-btn admin-btn--ghost"
          onClick={fetchReviews}
          disabled={loading}
        >
          ↻ Rafraîchir
        </button>
      </header>

      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : error ? (
        <p className="admin-empty" style={{ color: '#c00' }}>{error}</p>
      ) : reviews.length === 0 ? (
        <p className="admin-empty">Aucun avis pour le moment.</p>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Note</th>
                <th>Avis</th>
                <th>Date</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map(r => {
                const isEditing = editingId === r.id;
                const isDeleting = deletingId === r.id;

                return (
                  <tr
                    key={r.id}
                    style={isDeleting ? { opacity: 0.4 } : undefined}
                  >
                    <td>{r.user?.name || '—'}</td>

                    {/* Note */}
                    <td>
                      {isEditing ? (
                        <select
                          value={editNote}
                          onChange={(e) => setEditNote(Number(e.target.value))}
                          className="admin-input"
                        >
                          {[1, 2, 3, 4, 5].map(n => (
                            <option key={n} value={n}>
                              {'★'.repeat(n)}{'☆'.repeat(5 - n)} ({n})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span style={{ color: '#F59E0B', letterSpacing: '2px' }}>
                          {'★'.repeat(r.note || 0)}{'☆'.repeat(5 - (r.note || 0))}
                        </span>
                      )}
                    </td>

                    {/* Commentaire */}
                    <td style={{ maxWidth: 380 }}>
                      {isEditing ? (
                        <textarea
                          value={editComment}
                          onChange={(e) => setEditComment(e.target.value)}
                          rows={3}
                          maxLength={1000}
                          className="admin-input"
                          style={{ width: '100%', resize: 'vertical' }}
                        />
                      ) : (
                        <span>{r.commentaire || r.comment || '—'}</span>
                      )}
                    </td>

                    {/* Date */}
                    <td>
                      {r.created_at
                        ? new Date(r.created_at).toLocaleDateString('fr-FR')
                        : '—'}
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <button
                            className="admin-btn admin-btn--primary"
                            onClick={() => saveEdit(r.id)}
                            disabled={saving}
                          >
                            {saving ? '…' : '✓ Enregistrer'}
                          </button>
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={cancelEdit}
                            disabled={saving}
                            style={{ marginLeft: 6 }}
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={() => startEdit(r)}
                            disabled={isDeleting}
                          >
                            ✏️ Modifier
                          </button>
                          <button
                            className="admin-btn admin-btn--danger"
                            onClick={() => deleteReview(r.id)}
                            disabled={isDeleting}
                            style={{ marginLeft: 6 }}
                          >
                            {isDeleting ? '…' : '🗑 Supprimer'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminReviews;